import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Pagination from '../components/Pagination';
import PageHeader from '../components/PageHeader';
import UserDirectoryToolbar from '../components/users/UserDirectoryToolbar';
import PlanCell from '../components/users/PlanCell';
import TrustedContactsCell from '../components/users/TrustedContactsCell';
import XmitCell from '../components/users/XmitCell';
import DeleteUserModal from '../components/users/DeleteUserModal';
import AssignXmitModal from '../components/users/AssignXmitModal';
import RemoveXmitModal from '../components/users/RemoveXmitModal';
import ToastBanner from '../components/ToastBanner';
import { useUserDirectory } from '../hooks/useUserDirectory';
import { deleteUserApi, assignXmitApi, changeXmitApi, removeXmitApi } from '../api/users.api';
import { apiHandler } from '../utils/apiHandler';

function getInitials(firstName, lastName) {
  const name = `${firstName || ''} ${lastName || ''}`.trim();
  if (!name) return '??';
  return name.split(' ').map((x) => x[0]).join('').slice(0, 2).toUpperCase();
}

function ActivityCell({ activity }) {
  if (!activity) return <span style={{ color: '#94A3B8', fontSize: 12 }}>Never</span>;
  if (activity.label === 'Today') {
    return (
      <span style={{ color: '#2E7D32', fontWeight: 600, fontSize: 12 }}>
        <i className="fas fa-circle" style={{ fontSize: 7, marginRight: 3 }} />
        Today
      </span>
    );
  }
  if (activity.label === 'Never') {
    return <span style={{ color: '#94A3B8', fontSize: 12 }}>Never</span>;
  }
  return <span style={{ color: '#94A3B8', fontSize: 12 }}>{activity.label} ago</span>;
}

function userDisplayName(user) {
  if (!user) return 'User';
  const name = `${user.firstName || ''} ${user.lastName || ''}`.trim();
  return name || user.email || 'User';
}

/** 3-dot kebab menu for each user row */
function RowActions({ user, onNavigate, onDelete, onAssignXmit, onChangeXmit, onRemoveXmit }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const hasOrg = !!user.organization;

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        type="button"
        className="action-btn"
        title="More actions"
        onClick={(e) => { e.stopPropagation(); setOpen((p) => !p); }}
        style={{
          background: open ? '#F1F5F9' : 'transparent',
          border: '1px solid transparent',
          color: '#64748B',
          padding: '5px 10px',
          borderRadius: 8,
          fontSize: 14,
          cursor: 'pointer',
          transition: 'background 0.15s',
        }}
      >
        <i className="fas fa-ellipsis-vertical" />
      </button>

      {open && (
        <div
          style={{
            position: 'absolute',
            right: 0,
            top: 'calc(100% + 4px)',
            background: '#fff',
            border: '1px solid #E2E8F0',
            borderRadius: 10,
            boxShadow: '0 8px 24px rgba(0,0,0,.10)',
            minWidth: 200,
            zIndex: 1000,
            overflow: 'hidden',
            animation: 'fadeInDown 0.12s ease',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* View Profile */}
          <button
            type="button"
            onClick={() => { setOpen(false); onNavigate(`/users/${user.id}`); }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              width: '100%',
              padding: '10px 14px',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: 13,
              color: '#0F172A',
              fontWeight: 500,
              textAlign: 'left',
              transition: 'background 0.12s',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = '#F8FAFC')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
          >
            <i className="fas fa-user" style={{ fontSize: 12, color: '#074f78', width: 16 }} />
            View Profile
          </button>

          <div style={{ height: 1, background: '#F1F5F9', margin: '0 10px' }} />

          {/* View Organization */}
          {hasOrg ? (
            <button
              type="button"
              onClick={() => { setOpen(false); onNavigate(`/organizations/${user.organization.id}?fromUser=${user.id}`); }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                width: '100%',
                padding: '10px 14px',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: 13,
                color: '#0F172A',
                fontWeight: 500,
                textAlign: 'left',
                transition: 'background 0.12s',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = '#F8FAFC')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
            >
              <i className="fas fa-building" style={{ fontSize: 12, color: '#ec7426', width: 16 }} />
              <span style={{ flex: 1 }}>View Organization</span>
              {user.organization.isActive && (
                <span style={{
                  fontSize: 10, fontWeight: 700, background: '#F0FDF4',
                  color: '#15803D', padding: '1px 6px', borderRadius: 8,
                  border: '1px solid #b7e5bb',
                }}>
                  Active
                </span>
              )}
            </button>
          ) : (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                width: '100%',
                padding: '10px 14px',
                fontSize: 13,
                color: '#94A3B8',
                cursor: 'not-allowed',
              }}
              title="This user has no organization"
            >
              <i className="fas fa-building" style={{ fontSize: 12, color: '#CBD5E1', width: 16 }} />
              View Organization
              <span style={{ fontSize: 10, color: '#CBD5E1', marginLeft: 'auto' }}>—</span>
            </div>
          )}

          <div style={{ height: 1, background: '#F1F5F9', margin: '0 10px' }} />

          {user.quickResponse?.xmitNumber ? (
            <>
              <button
                type="button"
                onClick={() => { setOpen(false); onChangeXmit(user); }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  width: '100%',
                  padding: '10px 14px',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: 13,
                  color: '#0369A1',
                  fontWeight: 600,
                  textAlign: 'left',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = '#F0F9FF')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
              >
                <i className="fas fa-arrows-rotate" style={{ fontSize: 12, width: 16 }} />
                Change XMIT
              </button>
              <button
                type="button"
                onClick={() => { setOpen(false); onRemoveXmit(user); }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  width: '100%',
                  padding: '10px 14px',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: 13,
                  color: '#B45309',
                  fontWeight: 600,
                  textAlign: 'left',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = '#FFFBEB')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
              >
                <i className="fas fa-tower-broadcast" style={{ fontSize: 12, width: 16 }} />
                Remove XMIT
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={() => { setOpen(false); onAssignXmit(user); }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                width: '100%',
                padding: '10px 14px',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: 13,
                color: '#0369A1',
                fontWeight: 600,
                textAlign: 'left',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = '#F0F9FF')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
            >
              <i className="fas fa-tower-broadcast" style={{ fontSize: 12, width: 16 }} />
              Assign XMIT
            </button>
          )}

          <div style={{ height: 1, background: '#F1F5F9', margin: '0 10px' }} />

          <button
            type="button"
            onClick={() => { setOpen(false); onDelete(user); }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              width: '100%',
              padding: '10px 14px',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: 13,
              color: '#B91C1C',
              fontWeight: 600,
              textAlign: 'left',
              transition: 'background 0.12s',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = '#FEF2F2')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
          >
            <i className="fas fa-trash-can" style={{ fontSize: 12, width: 16 }} />
            Delete
          </button>
        </div>
      )}
    </div>
  );
}

const exportBtnStyle = {
  background: '#F1F5F9',
  color: '#475569',
  border: '1px solid #E2E8F0',
  padding: '8px 14px',
  fontSize: 12,
};

export default function Users() {
  const navigate = useNavigate();
  const location = useLocation();
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [assignXmitTarget, setAssignXmitTarget] = useState(null);
  const [xmitModalVariant, setXmitModalVariant] = useState('assign');
  const [removeXmitTarget, setRemoveXmitTarget] = useState(null);
  const [assignXmitLoading, setAssignXmitLoading] = useState(false);
  const [removeXmitLoading, setRemoveXmitLoading] = useState(false);
  const [assignXmitError, setAssignXmitError] = useState('');
  const {
    users,
    filters,
    filterOptions,
    error,
    toast,
    loading,
    paginationProps,
    applyFilters,
    handleFilterChange,
    handleSearchChange,
    resetFilters,
    handleExport,
    refetchUsers,
    showToast,
  } = useUserDirectory({
    onboardingStatus: 'complete',
    exportFilename: 'active-users-export.csv',
  });

  useEffect(() => {
    if (location.state?.deletedUserToast) {
      showToast(
        location.state.deletedUserToast,
        location.state.deletedUserToastType || 'success',
        4000
      );
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate, showToast]);

  async function handleConfirmDelete(confirmation) {
    if (!deleteTarget) return;
    const targetName = userDisplayName(deleteTarget);
    setDeleteLoading(true);
    const result = await apiHandler(() => deleteUserApi(deleteTarget.id, confirmation));
    setDeleteLoading(false);

    if (result.success) {
      setDeleteTarget(null);
      showToast(`${targetName} deleted successfully`, 'success', 4000);
      refetchUsers();
      return;
    }

    showToast(
      `Delete failed: ${result.message || 'Could not delete user. Please try again.'}`,
      'error',
      5000
    );
  }

  async function handleConfirmAssignXmit({ mode, xmitNumber }) {
    if (!assignXmitTarget) return;
    setAssignXmitLoading(true);
    setAssignXmitError('');
    const apiCall = xmitModalVariant === 'change'
      ? () => changeXmitApi(assignXmitTarget.id, { mode, xmitNumber })
      : () => assignXmitApi(assignXmitTarget.id, { mode, xmitNumber });
    const result = await apiHandler(apiCall);
    setAssignXmitLoading(false);

    if (result.success) {
      setAssignXmitTarget(null);
      showToast(
        xmitModalVariant === 'change' ? 'XMIT changed successfully' : 'XMIT assigned successfully',
        'success'
      );
      refetchUsers();
      return;
    }

    if (result.code === 'XMIT_ALREADY_ON_USER') {
      setAssignXmitError('This user already has an XMIT assigned. Use Change XMIT instead.');
      return;
    }
    if (result.code === 'XMIT_ALREADY_ASSIGNED') {
      setAssignXmitError('This XMIT number is already assigned to another user.');
      return;
    }
    if (result.code === 'SAME_XMIT_NUMBER') {
      setAssignXmitError('Enter a different XMIT number from the current one.');
      return;
    }
    if (result.code === 'QR_PROVISION_FAILED' || result.code === 'QR_POOL_ASSIGN_FAILED') {
      setAssignXmitError('Quick Response could not create a new XMIT. Check server logs and QR credentials.');
      return;
    }
    setAssignXmitError(
      result.message || `Could not ${xmitModalVariant === 'change' ? 'change' : 'assign'} XMIT.`
    );
  }

  async function handleConfirmRemoveXmit(confirmation) {
    if (!removeXmitTarget) return;
    setRemoveXmitLoading(true);
    const result = await apiHandler(() =>
      removeXmitApi(removeXmitTarget.id, confirmation)
    );
    setRemoveXmitLoading(false);

    if (result.success) {
      setRemoveXmitTarget(null);
      showToast('XMIT removed successfully', 'success');
      refetchUsers();
      return;
    }

    showToast(result.message || 'Could not remove XMIT.', 'error', 5000);
  }

  return (
    <>
      <ToastBanner toast={toast} />

      <PageHeader
        inline
        trail={[{ label: 'Users', to: '/users' }, { label: 'Active Users' }]}
        actions={
          <button
            type="button"
            className="action-btn"
            style={exportBtnStyle}
            onClick={handleExport}
            disabled={loading}
          >
            <i className="fas fa-download" style={{ fontSize: 11 }} />
            Export
          </button>
        }
      />

      {error && (
        <div
          className="fade-up"
          style={{
            marginBottom: 12,
            padding: '12px 16px',
            background: '#FEF2F2',
            border: '1px solid #FECACA',
            borderRadius: 10,
            color: '#B91C1C',
            fontSize: 13,
          }}
        >
          {error}
        </div>
      )}

      <div className="fade-up delay-1">
        <div className="section-card">
          <UserDirectoryToolbar
            filters={filters}
            filterOptions={filterOptions}
            variant="complete"
            loading={loading}
            onFilterChange={handleFilterChange}
            onSearchChange={handleSearchChange}
            onApply={applyFilters}
            onReset={resetFilters}
          />

          <div style={{ overflowX: 'auto' }}>
            <table className="data-table" style={{ minWidth: 960 }}>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Mobile</th>
                  <th>XMIT</th>
                  <th>Plan</th>
                  <th>Trusted Contacts</th>
                  <th>Last Login</th>
                  <th style={{ width: 48, textAlign: 'right' }}></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={8} style={{ textAlign: 'center', padding: 24, color: '#64748B' }}>
                      Loading users…
                    </td>
                  </tr>
                ) : users.length === 0 ? (
                  <tr>
                    <td colSpan={8} style={{ textAlign: 'center', padding: 24, color: '#64748B' }}>
                      No users match your filters.
                    </td>
                  </tr>
                ) : (
                  users.map((u) => (
                    <tr
                      key={u.id}
                      className="user-row"
                      onClick={() => navigate(`/users/${u.id}`)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                          <div
                            style={{
                              width: 30,
                              height: 30,
                              borderRadius: 8,
                              background: 'linear-gradient(135deg,#102A43,#074f78)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: 11,
                              fontWeight: 700,
                              color: '#fff',
                              flexShrink: 0,
                            }}
                          >
                            {getInitials(u.firstName, u.lastName)}
                          </div>
                          <div>
                            <span style={{ fontWeight: 600, fontSize: 13, color: '#0F172A', display: 'block' }}>
                              {u.firstName} {u.lastName}
                            </span>
                            {u.organization && (
                              <span style={{ fontSize: 10, color: '#ec7426', fontWeight: 600 }}>
                                <i className="fas fa-building" style={{ marginRight: 3, fontSize: 9 }} />
                                {u.organization.groupName}
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td style={{ fontSize: 12, color: '#64748B' }}>{u.email || '—'}</td>
                      <td style={{ fontSize: 12, color: '#64748B', fontFamily: "'DM Mono',monospace" }}>
                        {u.mobile || '—'}
                      </td>
                      <td>
                        <XmitCell quickResponse={u.quickResponse} />
                      </td>
                      <td>
                        <PlanCell plan={u.plan} />
                      </td>
                      <td>
                        <TrustedContactsCell
                          trustedContacts={u.trustedContacts}
                          userName={u.fullName}
                        />
                      </td>
                      <td>
                        <ActivityCell activity={u.activity} />
                      </td>
                      <td
                        style={{ textAlign: 'right', paddingRight: 8 }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <RowActions
                          user={u}
                          onNavigate={navigate}
                          onDelete={setDeleteTarget}
                          onAssignXmit={(rowUser) => {
                            setAssignXmitError('');
                            setXmitModalVariant('assign');
                            setAssignXmitTarget(rowUser);
                          }}
                          onChangeXmit={(rowUser) => {
                            setAssignXmitError('');
                            setXmitModalVariant('change');
                            setAssignXmitTarget(rowUser);
                          }}
                          onRemoveXmit={setRemoveXmitTarget}
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
        <Pagination {...paginationProps} />
      </div>

      <DeleteUserModal
        user={deleteTarget}
        open={Boolean(deleteTarget)}
        loading={deleteLoading}
        onCancel={() => {
          if (!deleteLoading) setDeleteTarget(null);
        }}
        onConfirm={handleConfirmDelete}
      />

      <AssignXmitModal
        user={assignXmitTarget}
        variant={xmitModalVariant}
        open={Boolean(assignXmitTarget)}
        loading={assignXmitLoading}
        error={assignXmitError}
        onCancel={() => {
          if (!assignXmitLoading) setAssignXmitTarget(null);
        }}
        onConfirm={handleConfirmAssignXmit}
      />

      <RemoveXmitModal
        user={removeXmitTarget}
        xmitNumber={removeXmitTarget?.quickResponse?.xmitNumber}
        open={Boolean(removeXmitTarget)}
        loading={removeXmitLoading}
        onCancel={() => {
          if (!removeXmitLoading) setRemoveXmitTarget(null);
        }}
        onConfirm={handleConfirmRemoveXmit}
      />
    </>
  );
}
